/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
// formik
import { Formik } from 'formik';
import * as Yup from 'yup';
// @material-ui/core components
import withStyles from '@material-ui/core/styles/withStyles';
import TextField from '@material-ui/core/TextField';
import validationFormsStyle from 'assets/jss/material-dashboard-pro-react/views/validationFormsStyle';
// core components
import Card from 'components/UI/Card/Card';
import GridItem from 'components/UI/Grid/GridItem';
import CardBody from 'components/UI/Card/CardBody';
import Snackbars from 'components/UI/Snackbar/Snackbar';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
// our components
import { NOTIFICATION_CATEGORY } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import Loading from 'components/common/Loading';

/**
 * @description
 * Validation form's notification category.
 * @param {Function} t - Translation function from HOC.
 * @returns {Object} - YUP shape.
 */
const translateValidationSchema = t =>
  Yup.object({
    name: Yup.string().required(t('errors.required')),
    order: Yup.number()
      .integer(t('errors.integer'))
      .required(t('errors.required')),
    description: Yup.string().required(t('errors.required')),
  });

/**
 * @class
 * @extends React.Component
 */
class NotificationCategoryForm extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);

    const { id, name, order, description } = props;

    this.state = {
      fields: {
        id,
        name,
        order,
        description,
      },
    };

    this.formikRef = null;
  }

  /**
   * @description
   * Handle redirect of cancel btn.
   */
  handleCancelBtn = () => {
    const history = this.context;
    history.push(NOTIFICATION_CATEGORY.LIST);
  };

  /**
   * @description
   * Set server errors with setErrors function of Formik.
   * @param {*} serverErrors - Api errors.
   */
  setServerErrors = serverErrors => {
    let all = null;
    if (this.formikRef && serverErrors.length) {
      const errors = {};

      serverErrors.forEach(item => {
        errors[item.field] = item.messages;
        if (item.field === '__all__') {
          all = item.messages;
        }
      });

      setTimeout(() => this.formikRef && this.formikRef.setErrors(errors), 0);
    }
    return all;
  };

  /**
   * @description
   * Render component.
   *
   * @returns {*} - NotificationCategoryForm component.
   */
  render() {
    const {
      classes,
      t,
      notificationCategorySave,
      errors: serverErrors,
      closeNotification,
      submitLoading,
      editMode,
    } = this.props;
    const { fields } = this.state;
    const all = this.setServerErrors(serverErrors);
    const validationSchema = translateValidationSchema(t);

    return (
      <React.Fragment>
        <Snackbars
          place="tc"
          message={all || ''}
          open={!!all}
          color="danger"
          close
          closeNotification={closeNotification}
        />
        <Formik
          ref={ref => {
            this.formikRef = ref;
          }}
          initialValues={fields}
          validationSchema={validationSchema}
          onSubmit={values => notificationCategorySave({ ...values })}
          render={({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            handleBlur,
          }) => (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <GridItem className={classes.cardContainer}>
                <div
                  className={`${classes.row} ${classes.contentBtns}`}
                  style={{ justifyContent: 'space-between', display: 'flex' }}
                >
                  <h3>
                    {editMode
                      ? t('notificationCategory.edit')
                      : t('notificationCategory.create')}
                  </h3>
                </div>
                <form onSubmit={handleSubmit}>
                  <Card>
                    <Loading show={submitLoading} />
                    <CardBody>
                      <GridContainer direction="column" alignItems="center">
                        <GridItem xs={12} sm={10}>
                          <TextField
                            id="name"
                            label={t('notificationCategory.name')}
                            fullWidth
                            margin="normal"
                            value={values.name || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name ? errors.name : ''}
                          />
                          <TextField
                            id="order"
                            label={t('notificationCategory.order')}
                            fullWidth
                            margin="normal"
                            type="number"
                            value={values.order || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.order && Boolean(errors.order)}
                            helperText={touched.order ? errors.order : ''}
                          />
                          <TextField
                            id="description"
                            label={t('notificationCategory.description')}
                            fullWidth
                            margin="normal"
                            multiline
                            rows={4}
                            value={values.description || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.description && Boolean(errors.description)
                            }
                            helperText={
                              touched.description ? errors.description : ''
                            }
                          />
                          <div className={classes.section}>
                            <GridContainer direction="row-reverse">
                              <GridItem xs={12} sm={6}>
                                <div className={classes.contentBtns}>
                                  <Button
                                    id="save-notification-category"
                                    color="primary"
                                    type="submit"
                                    style={{ float: 'right' }}
                                  >
                                    {editMode
                                      ? t('common.update')
                                      : t('common.create')}
                                  </Button>
                                </div>
                              </GridItem>
                              <GridItem xs={12} sm={6}>
                                <Button
                                  id="cancel-notification-category"
                                  simple
                                  color="danger"
                                  onClick={this.handleCancelBtn}
                                  style={{ float: 'left' }}
                                >
                                  {t('common.cancel')}
                                </Button>
                              </GridItem>
                            </GridContainer>
                          </div>
                        </GridItem>
                      </GridContainer>
                    </CardBody>
                  </Card>
                </form>
              </GridItem>
            </div>
          )}
        />
      </React.Fragment>
    );
  }
}

/**
 * NotificationCategoryForm PropTypes
 * @param classes - styles received from withStyles
 * @param id - NotificationCategory ID
 * @param name - Name
 * @param order - Order
 * @param description - Description
 * @param errors - Errors data list
 * @param editMode - To active editing mode
 * @param submitLoading - To change to loading status
 * @param t - prop from HOC with string translations
 * @callback notificationCategorySave - callback called when user clicks save button
 * @callback closeNotification - callback to close notification
 */
NotificationCategoryForm.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  id: PropTypes.string,
  name: PropTypes.string,
  order: PropTypes.number,
  description: PropTypes.string,
  errors: PropTypes.instanceOf(Array).isRequired,
  submitLoading: PropTypes.bool.isRequired,
  notificationCategorySave: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
  closeNotification: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

NotificationCategoryForm.defaultProps = {
  id: undefined,
  name: '',
  order: 0,
  description: '',
  editMode: false,
};

export default withTranslation()(
  withStyles(validationFormsStyle)(NotificationCategoryForm),
);
